import { DocumentStatus } from '@synergy/models';
import { createSelector } from 'reselect';
import { hasErrors } from '@synergy/utils';

import { AppState } from '../../../../../store/models/app-state';
import { DemandAccountStatus } from '../../../models/demand-account-status';
import { DemandSteps } from '../models/demand-steps';
import { PoolStepperStepStates } from '../../../../../common/components/new-stepper/pool-stepper-step-states';

const demandsState = (state: AppState) => state.demandDetail;

export const transitionAvailableSelector = createSelector(demandsState, state => {
    const status = state.overview.stepData.values.status;
    const demandAccounts = state.feedback.stepData.values.participants;

    return (
        status === DocumentStatus.Completed &&
        demandAccounts.some(demandAccount =>
            [DemandAccountStatus.Accepted, DemandAccountStatus.PartiallyAccepted].includes(demandAccount.status)
        )
    );
});

export const transitionDataSelector = createSelector(demandsState, state => state.common.demandTransitionData);

export const contactCountPerTopicAndRegionSelector = createSelector(
    demandsState,
    state => state.topics.contactCountPerTopicAndRegion
);

export const demandDetailStepperStateSelector = createSelector(
    (state: AppState) => state.demandDetail.overview.stepData,
    (state: AppState) => state.demandDetail.positions.stepData,
    (state: AppState) => state.demandDetail.topics.stepData,
    (state: AppState) => state.demandDetail.participants.stepData,
    (state: AppState) => state.demandDetail.feedback.stepData,
    (state: AppState) => state.demandDetail.common.unlocked,
    (state: AppState) => state.demandDetail.common.completionStatus,
    (overviewState, positionsState, topicsState, participantsState, feedbackState, unlocked, completionStatus) => {
        const result: PoolStepperStepStates = {
            enabledSteps: [DemandSteps.Overview],
            errorSteps: [],
            doneSteps: [],
            readonlySteps: [],
        };

        if (!overviewState.values.id) {
            return result;
        }

        // Error states
        if (hasErrors(overviewState.errors)) {
            result.errorSteps.push(DemandSteps.Overview);
        }
        if (hasErrors(positionsState.errors)) {
            result.errorSteps.push(DemandSteps.Positions);
        }
        if (hasErrors(topicsState.errors)) {
            result.errorSteps.push(DemandSteps.Topics);
        }
        if (hasErrors(participantsState.errors)) {
            result.errorSteps.push(DemandSteps.Participants);
        }
        if (hasErrors(feedbackState.errors)) {
            result.errorSteps.push(DemandSteps.Feedback);
        }
        if (completionStatus.created) {
            result.doneSteps.push(DemandSteps.Overview);
            result.enabledSteps.push(DemandSteps.Positions);
        }
        if (completionStatus.positions) {
            result.doneSteps.push(DemandSteps.Positions);
            result.enabledSteps.push(DemandSteps.Topics);
        }
        if (completionStatus.topics) {
            result.doneSteps.push(DemandSteps.Topics);
            result.enabledSteps.push(DemandSteps.Participants);
        }
        if (completionStatus.participants) {
            result.doneSteps.push(DemandSteps.Participants);
            result.enabledSteps.push(DemandSteps.Feedback);
        }
        if (completionStatus.notifications) {
            result.doneSteps.push(DemandSteps.Feedback);
        }

        if (!unlocked) {
            if (overviewState.values.status === DocumentStatus.Scheduled) {
                result.readonlySteps.push(DemandSteps.Overview);
                result.readonlySteps.push(DemandSteps.Positions);
                result.readonlySteps.push(DemandSteps.Feedback);
            }

            if (overviewState.values.status === DocumentStatus.Ongoing) {
                result.readonlySteps.push(DemandSteps.Overview);
                result.readonlySteps.push(DemandSteps.Positions);
                result.readonlySteps.push(DemandSteps.Topics);
            }

            if (
                overviewState.values.status === DocumentStatus.Canceled ||
                overviewState.values.status === DocumentStatus.Completed ||
                overviewState.values.status === DocumentStatus.CompletedWithTransition ||
                overviewState.values.status === DocumentStatus.CompletedWithoutTransition
            ) {
                result.readonlySteps.push(DemandSteps.Overview);
                result.readonlySteps.push(DemandSteps.Positions);
                result.readonlySteps.push(DemandSteps.Topics);
                result.readonlySteps.push(DemandSteps.Participants);
                result.readonlySteps.push(DemandSteps.Feedback);
            }
        }

        return result;
    }
);

export const totalQuantityDemandPositionsSelector = createSelector(
    demandsState,
    state => state.common.totalQuantityDemandPositions
);
